<template>
    <div class="DriverForm">
        <DashboardEditSection title="Account">
            <FormInput
                v-model="$v.email.$model"
                label="Email"
                :errors="$v.email.$error ? [
                    !$v.email.required && 'Email or username is required',
                    !$v.email.email && 'Email is not valid',
                ] : null"
            />
            <FormInput
                v-model="$v.username.$model"
                label="Username"
                :errors="$v.username.$error ? [
                    !$v.username.required && 'Username or email is required',
                    !$v.username.minLength && 'Username has to be at least 4 characters long',
                    !$v.username.maxLength && 'Username cannot be more than 50 characters long',
                    !$v.username.username && 'Usernames must only contain alphanumeric characters. Hyphens, periods and underscores are allowed',
                ] : null"
                required
            />

            <div v-if="email && email.trim()" class="row">
                <FormToggle v-model="$v.sendSetupEmail.$model"/>
                <h4>Send Password Setup Email</h4>
            </div>

            <FormInput
                v-show="!sendSetupEmail"
                v-model="$v.password.$model"
                lazy
                type="password"
                label="Password"
                :errors="$v.password.$error ? [
                    !$v.password.required && 'Password is required',
                    !$v.password.minLength && 'Password has to be at least 6 characters long',
                ] : null"
                required
            />
            <FormInput
                v-show="!sendSetupEmail"
                v-model="$v.passwordConfirm.$model"
                lazy
                type="password"
                label="Repeat Password"
                :errors="$v.passwordConfirm.$error ? [
                    !$v.passwordConfirm.sameAsPassword && 'Passwords don\'t match',
                ] : null"
                required
            />
        </DashboardEditSection>

        <DashboardEditSection title="Information">
            <Row>
                <Column :md="6">
                    <FormInput
                        v-model="$v.firstName.$model"
                        label="First Name"
                        :errors="$v.firstName.$error ? [
                            !$v.firstName.required && 'First name is required',
                        ] : null"
                        required
                    />
                </Column>
                <Column :md="6">
                    <FormInput
                        v-model="$v.lastName.$model"
                        label="Last Name"
                        :errors="$v.lastName.$error ? [
                            !$v.lastName.required && 'Last name is required',
                        ] : null"
                        required
                    />
                </Column>
            </Row>
            <FormInput v-model="phone" label="Phone"/>

            <Row>
                <Column :md="6">
                    <FormInput v-model="di_employeeCode" label="Employee Code"/>
                </Column>
                <Column :md="6">
                    <FormDateInput v-model="di_hiredAt" label="Hire Date"/>
                </Column>
            </Row>

            <Row>
                <Column :md="6">
                    <FormSelect v-model="di_type" label="Employee Type">
                        <option value="employee">Employee</option>
                        <option value="subcontractor">Subcontractor</option>
                    </FormSelect>
                </Column>
                <Column :md="6">
                    <FormNumber v-model="di_rate" label="Pay Rate" :min="0"/>
                </Column>
            </Row>

            <Row>
                <Column :md="6">
                    <FormInput v-model="di_driverLicense" label="Driver License"/>
                </Column>
                <Column :md="6">
                    <FormDateInput v-model="di_medicalExpireAt" label="Medical Examination Expiration"/>
                </Column>
            </Row>
        </DashboardEditSection>

        <DashboardEditSection title="Equipment">
            <Row>
                <Column :md="6">
                    <FormSelectVehicle
                        v-model.number="di_primaryTruck"
                        label="Assigned Truck"
                        type="trucks"
                        vehicleType="truck"
                        showCreateOption
                        nullable
                    />
                </Column>
                <Column :md="6">
                    <FormSelectVehicle
                        v-model.number="di_primaryTrailer"
                        label="Assigned Trailer"
                        type="trailers"
                        vehicleType="trailer"
                        showCreateOption
                        nullable
                    />
                </Column>
            </Row>
        </DashboardEditSection>

        <DashboardEditSection title="Address">
            <AddressForm
                :address.sync="address"
                :city.sync="city"
                :state.sync="state"
                :zip.sync="zip"
            />
        </DashboardEditSection>

        <DriverNotificationsSection
            ref="notificationsSection"
            :channels.sync="di_dispatchNotificationChannels"
            :email="email"
            :phone="phone"
        />

        <EntityTagsSection :tags.sync="di_tags"/>
    </div>
</template>

<script>
import DashboardEditSection from '@/components/DashboardEditSection';
import AddressForm from '@/components/form/AddressForm';
import EntityTagsSection from '@/components/form/EntityTagsSection';
import DriverNotificationsSection from './DriverNotificationsSection.vue';
import { required, requiredUnless, minLength, maxLength, helpers } from 'vuelidate/lib/validators';
import FormToggle from '@/components/common/FormToggle.vue';
import Column from '@/components/common/Column.vue';

export default {
    name: 'DriverForm',

    components: {
        DashboardEditSection,
        AddressForm,
        FormToggle,
        Column,
        EntityTagsSection,
        DriverNotificationsSection,
    },

    syncProps: [
        'email',
        'username',
        'password',
        'firstName',
        'lastName',
        'phone',
        'address',
        'city',
        'state',
        'zip',
        'sendSetupEmail',
        'di_type',
        'di_employeeCode',
        'di_driverLicense',
        'di_rate',
        'di_hiredAt',
        'di_medicalExpireAt',
        'di_tags',
        'di_dispatchNotificationChannels',
        'di_primaryTruck',
        'di_primaryTrailer',
    ],

    data: () => ({
        passwordConfirm: '',
    }),

    validations() {
        return {
            email: {
                required: requiredUnless('username'),
                email: helpers.regex('email', /^[\S]+@[\S]+\.[\S]+$/i),
            },
            username: {
                required: requiredUnless('email'),
                minLength: minLength(4),
                maxLength: maxLength(50),
                username: helpers.regex('username', /^\w+[\w.-]+\w+$/),
            },
            password: {
                required: requiredUnless(() => this.sendSetupEmail),
                minLength: minLength(6),
            },
            passwordConfirm: {
                required: requiredUnless(() => this.sendSetupEmail),
                sameAsPassword: (value) => (this.sendSetupEmail || value === this.password),
            },
            firstName: { required },
            lastName: { required },
            sendSetupEmail: { required },
        }
    },

    watch: {
        email(email) {
            if (!email.trim() && this.sendSetupEmail) {
                this.$v.sendSetupEmail.$model = false;
            }
        },
    },

    methods: {
        validate() {
            this.$v.$touch();

            // Touch both before combining, so an invalid section still shows its error
            // even when the rest of the form is also invalid.
            const isSectionValid = this.$refs.notificationsSection.validate();

            return !this.$v.$invalid && isSectionValid;
        },
    },
}
</script>


<style lang="scss" scoped>
.DriverForm {
    .row {
        display: flex;
        align-items: center;
        margin-bottom: 15px;

        h4 {
            margin: 0;
        }

        .FormToggle {
            margin-right: 15px;
        }
    }
}
</style>
